import { useState, useEffect, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import apiDictionaries from 'api/dictionaries';
import apiFilters from 'api/filters';
import apiPermissions from 'api/permissions';
import apiUser from 'api/user';
import { initFilters } from 'actions/filters';
import { initAllConfiguration } from 'actions/configuration';
import { updateLayoutConfiguration } from 'actions/layoutConfiguration';
import { closeModal } from 'actions/modal';
import SocketService from 'helpers/SocketService';
import LocalStorage from 'helpers/LocalStorage';
import UserActivityWatcher from 'components/userActivityWatcher';
import App from './App';
import { RootState } from 'store';
import { WebSocketContext } from 'hooks/useWebSocketConnection';
import { setAppTheme } from 'theme';
import { updateBrandConfig } from 'actions/settings';
import breakpoints from 'constants/breakpoints';
import { selectAppIsReady } from 'selectors/getAppReady';
import Loader from 'components/ui/loader';

const AppContainer: FC = () => {
  const dispatch = useDispatch();
  const [isWSConnected, setWSConnected] = useState(false);
  const appInstanceId = useSelector(
    (state: RootState) => state.app?.instanceId
  );
  const userId = useSelector((state: RootState) => state.user?.id);
  const appIsReady = useSelector(selectAppIsReady);
  const isNewMenuAvailable = useSelector(
    (state: RootState) => state.settings.isNewMenuAvailable
  );

  useEffect(() => {
    setUserId();
    attachChangeUserIdListener();
    initializeData();

    return () => {
      dispatch(closeModal());
      SocketService.destroy();
    };
  }, []);

  const initializeData = async () => {
    try {
      const [ws] = await Promise.all([
        SocketService.init(),
        fetchTableFilters(),
        fetchTableColumns(),
        fetchPermissions(),
        fetchUserCustomTheme(),
        fetchMenuConfig(),
      ]);
      setWSConnected(ws.isOpened);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchTableColumns = async () => {
    try {
      const list = await apiDictionaries.getTableColumns();
      dispatch(initAllConfiguration(list));
    } catch (e) {
      console.error(e);
    }
  };

  const fetchTableFilters = async () => {
    try {
      const filters = await apiFilters.getFiltersList();
      dispatch(initFilters(filters));
    } catch (e) {
      console.error(e);
    }
  };

  const fetchPermissions = async () => {
    try {
      await apiPermissions.getPermissions();
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMenuConfig = async () => {
    try {
      const isMobile = window.innerWidth <= breakpoints.header;
      const menuExpandedRes = await apiUser.getConfig({
        param: 'isMenuExpanded',
      });

      if (!isNewMenuAvailable) {
        dispatch(
          updateLayoutConfiguration({
            isMenuExpanded: !isMobile
              ? (menuExpandedRes.isMenuExpanded ?? true)
              : false,
            menuVersion: 'old',
            isNewMenuNotified: true,
          })
        );
        return;
      }

      const [menuVersionRes, menuNotificationRes] = await Promise.all([
        apiUser.getConfig({ param: 'menuVersion' }),
        apiUser.getConfig({ param: 'isNewMenuNotified' }),
      ]);

      dispatch(
        updateLayoutConfiguration({
          isMenuExpanded: !isMobile
            ? (menuExpandedRes.isMenuExpanded ?? true)
            : false,
          menuVersion: menuVersionRes.menuVersion ?? 'new',
          isNewMenuNotified: menuNotificationRes.isNewMenuNotified ?? false,
        })
      );
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUserCustomTheme = async () => {
    try {
      const { customTheme } = await apiUser.getConfig({ param: 'customTheme' });
      if (customTheme) {
        const brandConfig = LocalStorage.get('brandConfig');
        if (!brandConfig || brandConfig?.id !== customTheme.id) {
          setAppTheme(customTheme.config.theme);
          dispatch(updateBrandConfig(customTheme));
          LocalStorage.set('brandConfig', customTheme);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const setUserId = () => {
    if (userId) {
      LocalStorage.set('userId', userId);
    }
  };

  const attachChangeUserIdListener = () => {
    LocalStorage.addEventListener((event) => {
      const { key, oldValue, newValue } = event;

      if (key === 'userId' && oldValue && newValue && oldValue !== newValue) {
        window.location.reload();
      }
    });
  };

  if (!appIsReady) {
    return (
      <div style={{ minHeight: '100vh' }}>
        <Loader />
      </div>
    );
  }

  return (
    <WebSocketContext.Provider value={isWSConnected}>
      <UserActivityWatcher instanceId={appInstanceId} />
      <App />
    </WebSocketContext.Provider>
  );
};

export default AppContainer;
